import * as React from 'react'
import { SettingsSectionWithTabs } from '../../components/SettingsSectionWithTabs'
import { FieldsetSelect } from '../../components/FieldsetSelect'
import { FieldsetSwitch } from '../../components/FieldsetSwitch'

import { Button } from '@/ui'
import {
  BREAK_MAPPING_DESTINATIONS,
  SHORT_BRAKE_MODES,
  SHORT_BREAK_TYPES,
  LUMPSUM_AMOUNTS_COVERED_OPTIONS,
  TIMESHEET_MINIMAL_PERIOD_SOURCES,
  ACTIVITY_TO_MAP_TYPES,
  MAPPING_ATIVITY_DESTINATIONS,
  PLANNED_HOURS_ROUNDINGS,
  CALCULATION_ROUNDINGS,
} from './options'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useWrapConfirm } from '@/modules/shared/hooks/useWrapConfirm'
import axios from 'axios'
import { toast } from '@/lib/toasts/api'
import { useQueryTimesheetAdditionalSettings } from '@/lib/api/company/configuration/timesheet-additional/useQueryTimesheetAdditionalSettings'
import { useMutationTimesheetAdditionalSettings } from '@/lib/api/company/configuration/timesheet-additional/useMutationTimesheetAdditionalSettings'
import { TimesheetAdditionalConfigurationRaw } from '@/lib/api/company/configuration/timesheet-additional/types'
import { HolidaysTable } from './HolidaysTable'

import { getBreaksMappingDestinationName } from '@/app/common/converters/configuration/breaksMappingDestinationToTextConverter'
import { getBreaksMappingDestination } from '@/app/services/configuration/breaksMappingDestinationResolver'
import { getTimesheetMinimalPeriodsSourceName } from '@/app/common/converters/configuration/timesheetMinimalPeriodsSourceToTextConverter'
import { getTimesheetMinimalPeriodsSource } from '@/app/services/configuration/timesheetMinimalPeriodsSourceResolver'
import { getShortBreakModeName } from '@/app/common/converters/configuration/shortBreakModeToTextConverter'
import { getShortBreakMode } from '@/app/services/configuration/shortBreakModeResolver'
import { getShortBreakTypeName } from '@/app/common/converters/configuration/shortBreakTypeToTextConverter'
import { getShortBreakType } from '@/app/services/configuration/shortBreakTypeResolver'
import { EventQualificationTable } from './EventQualificationTable'
import { getActivityToMapTypeName } from '@/app/common/converters/configuration/activityToMapTypeToTextConverter'
import { getActivityToMapType } from '@/app/services/configuration/activityToMapTypeResolver'
import { getMappingActivityDestinationName } from '@/app/common/converters/configuration/mappingActivityDestinationToTextConverter'
import { getMappingActivityDestination } from '@/app/services/configuration/mappingActivityDestinationResolver'
import { SettingsPageWrapper } from '../../components/SettingsPageWrapper'
import { formatDropdownTimespan } from '@/app/common/dateHelper'
import { getLumpSumAmountsCoveredOptionsName } from '@/app/common/converters/configuration/lumpSumAmountsCoveredOptionsToTextConverter'
import { getLumpSumAmountsCoveredOptions } from '@/app/services/configuration/lumpSumAmountsCoveredOptionsResolver'
import { RouteComponentProps } from '@/app/navigation/withRouter'
import { buildTimesheetAdditionalDiffLabels, TIMESHEET_ADDITIONAL_TAB_SCOPE } from './labels'
import { buildTimesheetAdditionalFormatters } from './formatters'
import { usePersistentTab } from '@/modules/shared/stores/tabsStore'
import { ConfirmModal } from '@/modules/drivers/modals/ConfirmModal'
import { ConfigurationType } from '@/app/constants/configuration/configurationType'
import { QUERY_KEY_SETTINGS_TIMESHEET_ADDITIONAL } from '@/lib/api/constants'

export const TimesheetAdditionalPageContent = ({
  match,
}: RouteComponentProps<{ companyId: string }>) => {
  const readOnly = false
  const companyId = match.params.companyId
  const [selectedTab, setSelectedTab] = usePersistentTab(
    TIMESHEET_ADDITIONAL_TAB_SCOPE,
    'a',
  )

  const wrapConfirm = useWrapConfirm()

  const {
    data,
    isFetching,
    error: queryError,
  } = useQueryTimesheetAdditionalSettings(companyId)

  const {
    mutateAsync,
    isPending,
    error: mutationError,
  } = useMutationTimesheetAdditionalSettings(companyId)

  const [showRecalcInfo, setShowRecalcInfo] = React.useState(false)

  const handleSave = async () => {
    if (state) {
      try {
        await mutateAsync(state)
        toast.success({ text: t('configuration.data-updated') })
        setShowRecalcInfo(true)
      } catch (e) {
        if (axios.isAxiosError(e)) {
          const data = e.response?.data
          if (Array.isArray(data) && data.length > 0) {
            const code = data[0]?.code
            let errorMsg = code
            try {
              errorMsg = t(`errorCode.${errorMsg}`)
            } catch (e) {}
            toast.error({ text: errorMsg, autoCloseMs: 5000 })
          } else {
            toast.error({ text: 'Unhandled Error' })
          }
        } else {
          toast.error({ text: 'Unknown Error' })
        }
      }
    }
  }

  const handleReset = React.useCallback(() => {
    setState(data)
  }, [data])

  const [state, setState] =
    React.useState<TimesheetAdditionalConfigurationRaw | null>(null)

  React.useEffect(() => {
    if (data) {
      setState(data)
    }
  }, [data])

  const { t } = useLocalize()

  const diffLabels = React.useMemo(() => buildTimesheetAdditionalDiffLabels(t), [t])

  const diffRenderers = React.useMemo<Record<string, (before: unknown, after: unknown) => { before: React.ReactNode; after: React.ReactNode }>>(() => ({
    activitiesMappingsConfiguration: (before, after) => {
      type Row = { orderIndex: number; activityToMapType: number; mappingActivityDestination: number; timeFrom?: string; timeTo?: string; ignoreRests: boolean; splitActivity: boolean; isNew?: boolean }
      const b = (before as Row[]) ?? []
      const a = (after as Row[]) ?? []
      const formatRow = (row: Row) => {
        const type = getActivityToMapTypeName(getActivityToMapType(row.activityToMapType), t)
        const dest = getMappingActivityDestinationName(getMappingActivityDestination(row.mappingActivityDestination), t)
        const time = row.timeFrom ? `${row.timeFrom}–${row.timeTo ?? ''}` : ''
        return [type, dest, time].filter(Boolean).join(' → ')
      }
      const added = a.filter(ar => ar.isNew || !b.some(br => br.orderIndex === ar.orderIndex))
      const removed = b.filter(br => !a.some(ar => ar.orderIndex === br.orderIndex))
      const changed = a.filter(ar => !ar.isNew && b.some(br => br.orderIndex === ar.orderIndex && JSON.stringify(br) !== JSON.stringify(ar)))
      if (!added.length && !removed.length && !changed.length) return { before: <span>—</span>, after: <span>—</span> }
      return {
        before: (
          <div className="tw-flex tw-flex-col tw-gap-1 tw-text-xs">
            {removed.map((r, i) => <div key={i} className="tw-line-through">{formatRow(r)}</div>)}
            {changed.map(r => {
              const br = b.find(br => br.orderIndex === r.orderIndex)
              return <div key={r.orderIndex} className="tw-line-through">{formatRow(br)}</div>
            })}
            {!removed.length && !changed.length && <span>—</span>}
          </div>
        ),
        after: (
          <div className="tw-flex tw-flex-col tw-gap-1 tw-text-xs">
            {added.map((r, i) => <div key={i}>{formatRow(r)}</div>)}
            {changed.map(r => <div key={r.orderIndex}>{formatRow(r)}</div>)}
            {!added.length && !changed.length && <span>—</span>}
          </div>
        ),
      }
    },
  }), [t])

  const diffFormatters = React.useMemo(() => buildTimesheetAdditionalFormatters(t), [t])

  const handleChangeBoolean = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, checked } = e.target
      setState((state) => ({
        ...state,
        [name]: checked,
      }))
    },
    [],
  )

  const handleSelect = React.useCallback((value, { name, option }) => {
    switch (name) {
      case 'breaksMapping':
      case 'shortBreakMode':
      case 'shortBreakType':
      case 'timesheetMinimalPeriods':
      case 'showAmountsCoveredByLumpsum':
        setState((state) => ({
          ...state,
          [name]: Number(value),
        }))
        break
      default:
        setState((state) => ({
          ...state,
          [name]: option?.key ?? value,
        }))
        break
    }
  }, [])

  const isDirty = React.useMemo(() => {
    return JSON.stringify(state) !== JSON.stringify(data)
  }, [state, data])

  return (
    <>
    <SettingsPageWrapper
      isReady={!!state}
      isPending={isPending || isFetching}
      isError={!!queryError || !!mutationError}
      isDirty={isDirty}
      errorMsg={
        queryError
          ? t('configuration-timesheet.additional-load-error')
          : ''
      }
      globalErrorMsg={t('configuration-timesheet.additional-load-error')}
      diffBefore={(data ?? {}) as Record<string, unknown>}
      diffAfter={(state ?? {}) as Record<string, unknown>}
      diffLabels={diffLabels}
      diffFormatters={diffFormatters}
      diffRenderers={diffRenderers}
      restore={{
        configurationType: ConfigurationType.TimesheetAdditional,
        tabLabelKey: 'configuration-menu.timesheet-additional',
        queryKeys: [QUERY_KEY_SETTINGS_TIMESHEET_ADDITIONAL],
        serverData: data,
        withDefaults: true,
      }}
      actions={
        <>
          <Button
            disabled={!isDirty}
            className="tw-rounded-lg tw-px-4 tw-text-content-secondary"
            variant="secondary"
            size="sm"
            onClick={wrapConfirm(handleReset, {
              message: t('configuration.reset-confirm'),
            })}
          >
            {t('common.reset')}
          </Button>
          <Button
            disabled={!isDirty}
            className="tw-rounded-lg tw-px-4"
            size="sm"
            onClick={wrapConfirm(handleSave, { message: t('configuration.save-confirm') })}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      {state && (
        <SettingsSectionWithTabs
          selected={selectedTab}
          setSelected={setSelectedTab}
          fullWidth
        >
          {/* TAB A: Identyfikacja zdarzeń */}
          <div
            data-tab-name="a"
            title={t('configuration-timesheet.tab-event-identification')}
            className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
          >
            {/* Okres pracy w pracy zmieniaj na -> BreaksMapping */}
            <FieldsetSelect
              label={t('configuration-timesheet.breaks-mapping')}
              className='lg:tw-max-w-[380px]'
              options={BREAK_MAPPING_DESTINATIONS.map((value) => ({
                key: value,
                value: value.toString(),
                label: getBreaksMappingDestinationName(
                  getBreaksMappingDestination(value),
                  t,
                ),
              }))}
              value={state.breaksMapping?.toString()}
              handleChange={handleSelect}
              name="breaksMapping"
              disabled={readOnly}
            />

            {/* Uwzględniaj zamianę zdarzeń dla wyliczenia płacy UE */}
            <FieldsetSwitch
              label={t('configuration-timesheet.map-activities-for-eu')}
              checked={state.mapActivitiesForMinimalWork}
              onChange={handleChangeBoolean}
              name="mapActivitiesForMinimalWork"
              disabled={readOnly}
            />

            {/* Min. wyg. sumy odcinków */}
            <FieldsetSwitch
              label={t('configuration-timesheet.map-only-minimal-breaks')}
              checked={state.mapOnlyMinimalBreaks}
              onChange={handleChangeBoolean}
              name="mapOnlyMinimalBreaks"
              disabled={readOnly}
            />

            <EventQualificationTable
              rows={state.activitiesMappingsConfiguration}
              setRows={(updater) =>
                setState((prev) => ({
                  ...prev,
                  activitiesMappingsConfiguration:
                    typeof updater === 'function'
                      ? updater(prev.activitiesMappingsConfiguration)
                      : updater,
                }))
              }
              activityTypeOptions={ACTIVITY_TO_MAP_TYPES.map((opt) => ({
                value: opt,
                label: getActivityToMapTypeName(getActivityToMapType(opt), t),
              }))}
              destinationOptions={MAPPING_ATIVITY_DESTINATIONS.map((opt) => ({
                value: opt,
                label: getMappingActivityDestinationName(
                  getMappingActivityDestination(opt),
                  t,
                ),
              }))}
            />
          </div>

          {/* TAB B: Opcje dodatkowe */}
          <div
            data-tab-name="b"
            title={t('configuration-timesheet.tab-additional-options')}
            className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
          >
            <FieldsetSelect
              label={t('configuration-timesheet.round-planned-hours')}
              options={PLANNED_HOURS_ROUNDINGS.map((value) => ({
                key: value.toString(),
                value: value.toString(),
                label: formatDropdownTimespan(value.toString(), t),
              }))}
              value={state.plannedHoursRounding}
              handleChange={handleSelect}
              name="plannedHoursRounding"
              disabled={readOnly}
            />

            <FieldsetSelect
              label={t('configuration-timesheet.round-calculation-values')}
              options={CALCULATION_ROUNDINGS.map((value) => ({
                key: value.toString(),
                value: value.toString(),
                label: formatDropdownTimespan(value.toString(), t),
              }))}
              value={state.calculationRounding}
              handleChange={handleSelect}
              name="calculationRounding"
              disabled={readOnly}
            />

            {/* Zaokrąglaj wszystkie wyliczone składniki ewidencji */}
            <FieldsetSwitch
              label={t('configuration-timesheet.round-all-components')}
              checked={state.roundAllTimesheetEvents}
              onChange={handleChangeBoolean}
              name="roundAllTimesheetEvents"
              disabled={readOnly}
            />

            {/* Urlop bezpłatny obniża zawsze ryczałty */}
            <FieldsetSwitch
              label={t('configuration-timesheet.unpaid-leave-reduces-lumpsum')}
              checked={state.notPaidLeaveAlywasReducePayment}
              onChange={handleChangeBoolean}
              name="notPaidLeaveAlywasReducePayment"
              disabled={readOnly}
            />

            {/* Rozliczaj ryczałty, premię i nagrodę proporcjonalnie do kwot dopłat UE */}
            <FieldsetSwitch
              label={t('configuration-timesheet.eu-payment-split-proportionally')}
              checked={state.euWorkAdditionalPaymentSplitProportionally}
              onChange={handleChangeBoolean}
              name="euWorkAdditionalPaymentSplitProportionally"
              disabled={readOnly}
            />

            {/* Oddawaj dni wolne za pracę w niedzielę i św. przed nadgodzinami */}
            <FieldsetSwitch
              label={t('configuration-timesheet.return-weekdays-before-overhours')}
              checked={state.returnWeekDaysBeforeOverhours}
              onChange={handleChangeBoolean}
              name="returnWeekDaysBeforeOverhours"
              disabled={readOnly}
            />

            {/* Przenoś nieoddane nadgodziny do oddania na następny miesiąc */}
            <FieldsetSwitch
              label={t('configuration-timesheet.postpone-overhours')}
              checked={state.postponeNotReturnedOverhours}
              onChange={handleChangeBoolean}
              name="postponeNotReturnedOverhours"
              disabled={readOnly}
            />

            {/* Obniżaj wynagrodzenie zasadnicze o wolne za oddane nadg. na wniosek i dyżur 100% */}
            <FieldsetSwitch
              label={t('configuration-timesheet.deduction-for-overtime-request')}
              checked={state.addDeductionForOvertimeAtRequest}
              onChange={handleChangeBoolean}
              name="addDeductionForOvertimeAtRequest"
              disabled={readOnly}
            />

            {/* Uwzględniaj premię do podstawy nadgodzin */}
            <FieldsetSwitch
              label={t('configuration-timesheet.include-bonus-in-overhours')}
              checked={state.includeBonusInOverhoursRate}
              onChange={handleChangeBoolean}
              name="includeBonusInOverhoursRate"
              disabled={readOnly}
            />

            <FieldsetSwitch
              label={t('configuration-timesheet.include-years-in-overhours')}
              checked={!!state.includeYearsAdditionInOverhoursRate}
              onChange={handleChangeBoolean}
              name="includeYearsAdditionInOverhoursRate"
              disabled={readOnly}
            />

            {/* Uwzględniaj dodatek UE do podstawy nadgodzin */}
            <FieldsetSwitch
              label={t('configuration-timesheet.include-eu-addition-in-overhours')}
              checked={state.includeEuAdditionInOverhoursRate}
              onChange={handleChangeBoolean}
              name="includeEuAdditionInOverhoursRate"
              disabled={readOnly}
            />

            {/* Uwzględniaj wyrównanie UE do podstawy nadgodzin */}
            <FieldsetSwitch
              label={t('configuration-timesheet.include-eu-work-to-pay-in-overhours')}
              checked={state.includeEuWorkToPayInOverhoursRate}
              onChange={handleChangeBoolean}
              name="includeEuWorkToPayInOverhoursRate"
              disabled={readOnly}
            />

            {/* Uwzględniaj zasadnicze wynagrodzenie o nieobecności */}
            {/* new [issues/1025]: obniżaj wynagrodzenie zasadnicze o nieobecności */}
            <FieldsetSwitch
              label={t('configuration-timesheet.reduce-base-payment-on-leaves')}
              checked={state.reduceBasePaymentOnLeaves}
              onChange={handleChangeBoolean}
              name="reduceBasePaymentOnLeaves"
              disabled={readOnly}
            />

            {/* Pobieraj granice do płacy minimalnej UE z */}
            <FieldsetSelect
              label={t('configuration-timesheet.minimal-periods-source')}
              options={TIMESHEET_MINIMAL_PERIOD_SOURCES.map((value) => ({
                key: value,
                value: value.toString(),
                label: getTimesheetMinimalPeriodsSourceName(
                  getTimesheetMinimalPeriodsSource(value),
                  t,
                ),
              }))}
              value={state.timesheetMinimalPeriods?.toString()}
              handleChange={handleSelect}
              name="timesheetMinimalPeriods"
              disabled={readOnly}
            />

            {/* Generuj przerwy w pracy według (ShortBreakType) */}
            <FieldsetSelect
              label={t('configuration-timesheet.short-break-type')}
              options={SHORT_BREAK_TYPES.map((value) => ({
                key: value,
                value: value.toString(),
                label: getShortBreakTypeName(getShortBreakType(value), t),
              }))}
              value={state.shortBreakType?.toString()}
              handleChange={handleSelect}
              name="shortBreakType"
              disabled={readOnly}
            />

            {/* Dodawaj przerwę P15 (ShortBreakMode) */}
            <FieldsetSelect
              label={t('configuration-timesheet.short-break-mode')}
              options={SHORT_BRAKE_MODES.map((value) => ({
                key: value,
                value: value.toString(),
                label: getShortBreakModeName(getShortBreakMode(value), t),
              }))}
              value={state.shortBreakMode?.toString()}
              handleChange={handleSelect}
              name="shortBreakMode"
              disabled={readOnly}
            />

            {/* Pomijaj sprawdzanie ulgi podatkowej dla kierowców poniżej 26 roku życia */}
            <FieldsetSwitch
              label={t('configuration-timesheet.skip-tax-exempt-under26')}
              checked={state.skipTaxExemptOutOfDate}
              onChange={handleChangeBoolean}
              name="skipTaxExemptOutOfDate"
              disabled={readOnly}
            />

            <HolidaysTable state={state} setState={setState} />
          </div>

          {/* TAB C: Prezentacja i wydruk */}
          <div
            data-tab-name="c"
            title={t('configuration-timesheet.tab-presentation-print')}
            className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
          >
            <FieldsetSwitch
              label={t('configuration-timesheet.print-amounts')}
              checked={state.printTimesheetAmounts}
              onChange={handleChangeBoolean}
              name="printTimesheetAmounts"
              disabled={readOnly}
            />

            <FieldsetSwitch
              label={t('configuration-timesheet.print-foreign-to-pay')}
              checked={state.printForeignTimesheetToPayAmount}
              onChange={handleChangeBoolean}
              name="printForeignTimesheetToPayAmount"
              disabled={readOnly}
            />

            <FieldsetSwitch
              label={t('configuration-timesheet.print-leave-applications')}
              checked={state.printTimesheetLeaveApplications}
              onChange={handleChangeBoolean}
              name="printTimesheetLeaveApplications"
              disabled={readOnly}
            />

            <FieldsetSwitch
              label={t('configuration-timesheet.display-decimal-format')}
              checked={state.dispalyTimeSpanInDecimalFormat}
              onChange={handleChangeBoolean}
              name="dispalyTimeSpanInDecimalFormat"
              disabled={readOnly}
            />

            <FieldsetSwitch
              label={t('configuration-timesheet.print-duty')}
              checked={state.printDutyOnTimesheet}
              onChange={handleChangeBoolean}
              name="printDutyOnTimesheet"
              disabled={readOnly}
            />

            <FieldsetSwitch
              label={t('configuration-timesheet.print-reward-confirmation')}
              checked={state.printRewardConfirmation}
              onChange={handleChangeBoolean}
              name="printRewardConfirmation"
              disabled={readOnly}
            />

            <FieldsetSelect
              label={t('configuration-timesheet.lumpsum')}
              options={LUMPSUM_AMOUNTS_COVERED_OPTIONS.map((value) => ({
                key: value.toString(),
                value: value.toString(),
                label: getLumpSumAmountsCoveredOptionsName(getLumpSumAmountsCoveredOptions(value), t),
              }))}
              value={state.showAmountsCoveredByLumpsum}
              handleChange={handleSelect}
              name="showAmountsCoveredByLumpsum"
              disabled={readOnly}
            />
          </div>
        </SettingsSectionWithTabs>
      )}
    </SettingsPageWrapper>

    <ConfirmModal
      isOpen={showRecalcInfo}
      onClose={() => setShowRecalcInfo(false)}
      title={t('timesheet.settings-changed-title')}
      text={t('timesheet.settings-changed-per-driver-info')}
      actionText=""
      cancelText={t('answer.ok')}
    />
    </>
  )
}
